import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronRightIcon, ChevronLeftIcon, CheckIcon, InfoIcon } from 'lucide-react';
import { ProductOption, CartItemType } from '../types/types';
import { BOWL_OPTIONS, INGREDIENT_IMAGES } from '../data/menuData';
import { CONFIG } from '../data/config';
import { useLanguage } from '../context/LanguageContext';
interface BowlBuilderProps {
  onClose: () => void;
  onSave: (item: CartItemType) => void;
  initialItem?: CartItemType;
}
export function BowlBuilder({
  onClose,
  onSave,
  initialItem
}: BowlBuilderProps) {
  const {
    t,
    formatPrice,
    translateIngredient,
    translateBowlStep,
    translateProductName
  } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState<any>(initialItem?.selections || {
    base: null,
    proteina: null,
    toppings: [],
    toppingsPremium: [],
    aderezos: [],
    semillas: [],
    comentarios: ''
  });
  const STEPS = [{
    id: 'base',
    emoji: '🥗',
    options: BOWL_OPTIONS.bases,
    required: true,
    multi: false
  }, {
    id: 'proteina',
    emoji: '🥩',
    options: BOWL_OPTIONS.proteinas,
    required: true,
    multi: false
  }, {
    id: 'toppings',
    emoji: '🍅',
    options: BOWL_OPTIONS.toppings,
    required: false,
    multi: true,
    max: 4
  }, {
    id: 'toppingsPremium',
    emoji: '🥑',
    options: BOWL_OPTIONS.toppingsPremium,
    required: false,
    multi: true,
    max: 3
  }, {
    id: 'aderezos',
    emoji: '🥣',
    options: BOWL_OPTIONS.aderezos,
    required: false,
    multi: true,
    max: 2
  }, {
    id: 'semillas',
    emoji: '🥜',
    options: BOWL_OPTIONS.semillas,
    required: false,
    multi: true,
    max: 2
  }, {
    id: 'comentarios',
    emoji: '📝',
    options: [] as ProductOption[],
    required: false,
    multi: false
  }];
  const step = STEPS[currentStep];
  const stepTranslation = translateBowlStep(step.id);
  const handleSelect = (option: ProductOption) => {
    if (step.multi) {
      const currentSelections = selections[step.id] || [];
      const exists = currentSelections.find((item: ProductOption) => item.id === option.id);
      if (exists) {
        setSelections({
          ...selections,
          [step.id]: currentSelections.filter((item: ProductOption) => item.id !== option.id)
        });
      } else {
        if (currentSelections.length >= (step.max || 1)) return;
        setSelections({
          ...selections,
          [step.id]: [...currentSelections, option]
        });
      }
    } else {
      setSelections({
        ...selections,
        [step.id]: option
      });
      // Auto-advance on single select if not the last step
      if (currentStep < STEPS.length - 1) {
        setTimeout(nextStep, 300);
      }
    }
  };
  const nextStep = () => {
    if (currentStep < STEPS.length - 1) setCurrentStep(s => s + 1);
  };
  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  };
  const isStepValid = () => {
    if (!step.required) return true;
    if (step.multi) return (selections[step.id]?.length || 0) > 0;
    return !!selections[step.id];
  };
  const calculateSubtotal = () => {
    let total = 0;
    if (selections.base) total += selections.base.price;
    if (selections.proteina) total += selections.proteina.price;
    selections.toppings?.forEach((t: ProductOption) => total += t.price);
    selections.toppingsPremium?.forEach((t: ProductOption) => total += t.price);
    selections.aderezos?.forEach((t: ProductOption) => total += t.price);
    selections.semillas?.forEach((t: ProductOption) => total += t.price);
    return total;
  };
  const handleSave = () => {
    const item: CartItemType = {
      id: initialItem?.id || `bowl_${Date.now()}`,
      productId: 'custom_bowl',
      name: 'Bowl Personalizado',
      basePrice: 0,
      quantity: initialItem?.quantity || 1,
      selections,
      subtotal: calculateSubtotal(),
      type: 'bowl'
    };
    onSave(item);
  };
  const getSelectionCount = () => {
    if (!step.multi) return selections[step.id] ? 1 : 0;
    return selections[step.id]?.length || 0;
  };
  // Get all selected ingredients for visual progress
  const getAllSelectedIngredients = () => {
    const all: ProductOption[] = [];
    if (selections.base) all.push(selections.base);
    if (selections.proteina) all.push(selections.proteina);
    if (selections.toppings) all.push(...selections.toppings);
    if (selections.toppingsPremium) all.push(...selections.toppingsPremium);
    if (selections.aderezos) all.push(...selections.aderezos);
    if (selections.semillas) all.push(...selections.semillas);
    return all;
  };
  const selectedIngredients = getAllSelectedIngredients();
  const subtotalFormatted = formatPrice(calculateSubtotal());
  return <motion.div initial={{
    opacity: 0,
    y: 50
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 50
  }} className="fixed inset-0 z-50 bg-carbon flex flex-col max-w-md mx-auto shadow-2xl" data-id="element-885">
      {/* Header */}
      <div className="bg-surface px-4 py-4 flex items-center justify-between shadow-sm z-20 border-b border-gray-800" data-id="element-886">
        <button onClick={onClose} className="p-2 -ml-2 text-gray-400 hover:text-white" data-id="element-887">
          <XIcon className="w-6 h-6" data-id="element-888" />
        </button>
        <h2 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-889">
          {t('buildYourBowl')}
        </h2>
        <div className="w-10" data-id="element-890"></div>
      </div>

      {/* Progress Bar */}
      <div className="bg-surface-light h-1.5 w-full" data-id="element-891">
        <motion.div className="h-full bg-verde" initial={{
        width: 0
      }} animate={{
        width: `${(currentStep + 1) / STEPS.length * 100}%`
      }} transition={{
        ease: 'easeInOut'
      }} data-id="element-892" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-24" data-id="element-893">
        <AnimatePresence mode="wait" data-id="element-894">
          <motion.div key={currentStep} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-895">
            <div className="mb-6" data-id="element-896">
              <div className="flex items-center gap-2 mb-1" data-id="element-897">
                <span className="text-2xl" data-id="element-898">{step.emoji}</span>
                <h3 className="text-2xl font-heading font-bold text-white tracking-wide" data-id="element-899">
                  {stepTranslation.title}
                </h3>
              </div>
              <div className="flex items-center justify-between" data-id="element-900">
                <p className="text-gray-400 text-sm" data-id="element-901">
                  {stepTranslation.subtitle}
                </p>
                {step.required ? <span className="text-xs font-medium text-terracota bg-terracota/10 px-2 py-0.5 rounded-full border border-terracota/20" data-id="element-902">
                    {t('required')}
                  </span> : <span className="text-xs font-medium text-gray-400 bg-surface-light px-2 py-0.5 rounded-full border border-gray-700" data-id="element-903">
                    {t('optional')}
                  </span>}
              </div>
            </div>

            {/* Selection Counter for Multi-select */}
            {step.multi && <div className="mb-4 flex items-center justify-between bg-surface-light p-3 rounded-xl border border-gray-800" data-id="element-904">
                <span className="text-sm text-gray-300" data-id="element-905">{t('selected')}:</span>
                <span className={`text-sm font-bold ${getSelectionCount() === step.max ? 'text-terracota' : 'text-verde'}`} data-id="element-906">
                  {getSelectionCount()} / {step.max}
                </span>
              </div>}

            {stepTranslation.note && <p className="text-terracota text-sm mb-4 font-medium" data-id="element-907">
                {stepTranslation.note}
              </p>}

            {step.id === 'comentarios' ? <div data-id="element-908">
                <textarea value={selections.comentarios} onChange={e => setSelections({
              ...selections,
              comentarios: e.target.value
            })} placeholder={t('commentsPlaceholder')} className="w-full h-32 p-4 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none text-white placeholder-gray-500" data-id="element-909" />
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1" data-id="element-910">
                  <InfoIcon className="w-3 h-3" data-id="element-911" />
                  {t('specialInstructions')}
                </p>

                {/* Visual Preview — Stacked Bowl Composition */}
                {selectedIngredients.length > 0 && <div className="mt-6" data-id="element-912">
                    <h4 className="font-heading font-semibold text-base mb-3 text-white tracking-wide" data-id="element-913">
                      {t('yourBowl')}
                    </h4>
                    <div className="rounded-2xl overflow-hidden border border-gray-800 shadow-sm bg-surface-light relative" data-id="element-914">
                      <div className="relative w-full h-52 flex items-center justify-center" data-id="element-915">
                        {/* Background bowl image */}
                        <div className="absolute inset-0 opacity-10" data-id="element-916">
                          <img src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=600&h=400&fit=crop" alt="" className="w-full h-full object-cover blur-sm" data-id="element-917" />
                        </div>
                        {/* Ingredient stack */}
                        <div className="relative w-48 h-48" data-id="element-918">
                          {selectedIngredients.map((ing, idx) => {
                      const total = selectedIngredients.length;
                      const angle = idx / total * 360;
                      const radius = total <= 3 ? 38 : 52;
                      const x = Math.cos(angle * Math.PI / 180) * radius;
                      const y = Math.sin(angle * Math.PI / 180) * radius;
                      const size = total <= 2 ? 84 : total <= 4 ? 72 : 60;
                      const rotation = idx * 12 - 15;
                      return <div key={ing.id} className="absolute rounded-full overflow-hidden shadow-xl border-2 border-gray-700" style={{
                        width: size,
                        height: size,
                        left: `calc(50% + ${x}px - ${size / 2}px)`,
                        top: `calc(50% + ${y}px - ${size / 2}px)`,
                        zIndex: idx + 1,
                        transform: `rotate(${rotation}deg)`
                      }} data-id="element-919">
                                {INGREDIENT_IMAGES[ing.id] ? <img src={INGREDIENT_IMAGES[ing.id]} alt={translateIngredient(ing.id, ing.name)} className="w-full h-full object-cover" data-id="element-920" /> : <div className="w-full h-full bg-surface flex items-center justify-center text-white font-bold text-xs" data-id="element-921">
                                    {translateIngredient(ing.id, ing.name).charAt(0)}
                                  </div>}
                              </div>;
                    })}
                        </div>
                      </div>
                      <div className="bg-surface px-4 py-3 border-t border-gray-800" data-id="element-922">
                        <div className="flex flex-wrap gap-1.5 justify-center" data-id="element-923">
                          {selectedIngredients.map(ing => <span key={ing.id} className="text-[11px] bg-surface-light text-gray-300 px-2.5 py-1 rounded-full font-medium border border-gray-700" data-id="element-924">
                              {translateIngredient(ing.id, ing.name)}
                            </span>)}
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-2" data-id="element-925">
                          {selectedIngredients.length}{' '}
                          {t('ingredientsSelected')}
                        </p>
                      </div>
                    </div>
                  </div>}
              </div> : <div className="space-y-3" data-id="element-926">
                {step.options.map(option => {
              const isSelected = step.multi ? selections[step.id]?.some((item: ProductOption) => item.id === option.id) : selections[step.id]?.id === option.id;
              return <button key={option.id} onClick={() => handleSelect(option)} className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${isSelected ? 'border-verde bg-verde/10' : 'border-gray-700 bg-surface hover:border-gray-500'}`} data-id="element-927">
                      <div className="flex items-center gap-3" data-id="element-928">
                        {INGREDIENT_IMAGES[option.id] && <img src={INGREDIENT_IMAGES[option.id]} alt="" className={`w-10 h-10 rounded-full object-cover border ${isSelected ? 'border-verde' : 'border-gray-700'}`} loading="lazy" data-id="element-929" />}
                        <span className={`font-medium ${isSelected ? 'text-verde' : 'text-gray-200'}`} data-id="element-930">
                          {translateIngredient(option.id, option.name)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-931">
                        {option.price > 0 && <span className={`text-sm ${isSelected ? 'text-verde/80' : 'text-gray-400'}`} data-id="element-932">
                            +{formatPrice(option.price).primary}
                          </span>}
                        {option.price === 0 && <span className="text-xs text-gray-500" data-id="element-933">
                            {t('included')}
                          </span>}
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center border ${isSelected ? 'bg-verde border-verde text-carbon' : 'border-gray-600'}`} data-id="element-934">
                          {isSelected && <CheckIcon className="w-4 h-4" data-id="element-935" />}
                        </div>
                      </div>
                    </button>;
            })}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="bg-surface border-t border-gray-800 p-4 flex items-center justify-between gap-3 sticky bottom-0 z-20" style={{
      paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))'
    }} data-id="element-936">
        {currentStep > 0 ? <button onClick={prevStep} className="p-3 rounded-xl bg-surface-light text-gray-400 hover:text-white transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center border border-gray-700" data-id="element-937">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-938" />
          </button> : <div className="w-[48px]" data-id="element-939"></div>}

        <div className="flex-1 flex flex-col items-center" data-id="element-940">
          <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold" data-id="element-941">
            {t('subtotal')}
          </span>
          <div className="flex flex-col items-center" data-id="element-942">
            <span className="font-bold text-lg text-white" data-id="element-943">
              {subtotalFormatted.primary}
            </span>
            <span className="text-xs text-gray-400" data-id="element-944">
              {subtotalFormatted.secondary}
            </span>
          </div>
        </div>

        {currentStep < STEPS.length - 1 ? <button onClick={nextStep} disabled={!isStepValid()} className={`px-5 py-3 rounded-xl font-semibold flex items-center gap-2 transition-colors min-h-[48px] border border-white/10 ${isStepValid() ? 'bg-verde text-carbon shadow-lg shadow-verde/20' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`} data-id="element-945">
            {!step.required && getSelectionCount() === 0 ? t('skip') : t('next')}{' '}
            <ChevronRightIcon className="w-5 h-5" data-id="element-946" />
          </button> : <button onClick={handleSave} className="px-5 py-3 rounded-xl font-semibold flex items-center gap-2 bg-terracota text-white shadow-lg shadow-terracota/20 transition-colors min-h-[48px] border border-white/10" data-id="element-947">
            {t('add')} <CheckIcon className="w-5 h-5" data-id="element-948" />
          </button>}
      </div>
    </motion.div>;
}